import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useWhoami } from '@/hooks/useWhoami';
import { AddCredentialModal } from '@/sections/asset/components/tabs/credentials/AddCredentialModal';
import {
  buildBrokerPayload,
  type CredentialSubmitPayload,
  CredentialType,
  type IdentitySubmitResult,
  submitComplexLoginIdentities,
  useCredentialFormState,
} from '@/sections/asset/components/tabs/credentials/credentialForms';
import { CredentialListModal } from '@/sections/asset/components/tabs/credentials/CredentialListModal';
import { extractWebAppUrl } from '@/sections/asset/components/tabs/credentials/extractWebAppUrl';
import { useRecorderJobs } from '@/sections/asset/components/tabs/credentials/RecorderProgress';
import { makeComplexLoginSubmitOne } from '@/sections/asset/components/tabs/credentials/submitComplexLoginBroker';
import { useMutation } from '@/utils/api';

import { type CredentialManagerReturn } from './useCredentialManager';

interface CredentialModalsProps {
  manager: CredentialManagerReturn;
  isWebService: boolean;
}

const MANAGE_INTEGRATIONS_REQUIRED_MESSAGE =
  'You need the Manage Integrations entitlement to add credentials. Ask an administrator to update your permissions.';

const CHECKING_PERMISSIONS_MESSAGE = 'Checking credential permissions...';

function getAddCredentialErrorMessage(error: unknown, fallback: string) {
  if (error && typeof error === 'object') {
    const maybeStatus = (error as { status?: unknown }).status;
    const maybeName = (error as { name?: unknown }).name;
    const maybeMessage = (error as { message?: unknown }).message;

    if (
      maybeStatus === 403 ||
      (typeof maybeName === 'string' &&
        maybeName.toLowerCase().includes('manage_integrations')) ||
      (typeof maybeMessage === 'string' &&
        maybeMessage.toLowerCase().includes('manage_integrations'))
    ) {
      return MANAGE_INTEGRATIONS_REQUIRED_MESSAGE;
    }

    if (
      typeof maybeName === 'string' &&
      maybeName &&
      maybeName !== 'Error' &&
      maybeName !== 'Failed to add credential'
    ) {
      return maybeName;
    }

    if (typeof maybeMessage === 'string' && maybeMessage) {
      return maybeMessage;
    }
  }

  return fallback;
}

export function CredentialModals({
  manager,
  isWebService,
}: CredentialModalsProps) {
  const axios = useAxios();
  const { hasEntitlement, isLoading: entitlementsLoading } = useWhoami();
  const canAddCredentials =
    hasEntitlement('manage_integrations') && !entitlementsLoading;
  const addDisabledTooltip = entitlementsLoading
    ? CHECKING_PERMISSIONS_MESSAGE
    : MANAGE_INTEGRATIONS_REQUIRED_MESSAGE;

  const webAppUrl = extractWebAppUrl(manager.assetKey);

  // Modal step for the add-credential wizard
  const [modalStep, setModalStep] = useState<'type' | 'form' | 'progress'>(
    'type'
  );

  // Recorder-progress mode: one entry per succeeded identity (stacked recorder
  // hosts), plus the identities that failed to spawn a recorder (surfaced as a
  // per-identity failure list). Populated by the Complex-Login submit.
  const [recorderEntries, setRecorderEntries] = useState<
    { id: string; label: string; conversationId: string }[]
  >([]);
  const [failedIdentities, setFailedIdentities] = useState<
    { id: string; label: string }[]
  >([]);

  // Per-credential recording status, keyed by the recorder-agent job for this
  // asset. openRecorder re-opens the progress modal (AddCredentialModal in
  // 'progress' mode) subscribed to a given run so the user can watch it or see
  // whether it passed/failed.
  const { byCredential: recorderJobs } = useRecorderJobs(
    manager.group,
    manager.identifier,
    manager.modalState.kind === 'list'
  );
  const openRecorder = (conversationId: string, label: string) => {
    setRecorderEntries([{ id: conversationId, label, conversationId }]);
    setFailedIdentities([]);
    setModalStep('progress');
    manager.onAddClick();
  };

  // C5 fix: track close-timer so it can be cancelled if the modal reopens
  // within 500ms (prevents the old timer from wiping the new session's state).
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Per-type form state from the shared hook
  const {
    selectedType: selectedCredentialType,
    setSelectedType: setSelectedCredentialType,
    basicForm,
    setBasicForm,
    recordingForm,
    setRecordingForm,
    bearerTokenForm,
    setBearerTokenForm,
    apiKeyForm,
    setApiKeyForm,
    staticTokenForm,
    setStaticTokenForm,
    complexLoginMulti,
    setComplexLoginMulti,
    reset: resetCredentialForms,
  } = useCredentialFormState({ isWebService, webAppUrl });

  /* eslint-disable react-hooks/set-state-in-effect -- synchronize form state with asset selection */
  useEffect(() => {
    // C5 fix: cancel any pending close timer from a previous session so it
    // doesn't fire and wipe the form state for the new asset.
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setModalStep('type');
    resetCredentialForms();
    setRecorderEntries([]);
    setFailedIdentities([]);
  }, [manager.assetKey]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Add credential mutation — the single-POST path for every NON-complex
  // credential type. COMPLEX_LOGIN uses submitComplexIdentities (below), which
  // fans out one broker POST per identity.
  const { mutateAsync: addCredential, status: addCredentialStatus } =
    useMutation({
      mutationFn: async (payload: CredentialSubmitPayload) => {
        const body =
          payload.type === CredentialType.RECORDING
            ? buildBrokerPayload(
                {
                  type: CredentialType.RECORDING,
                  data: {
                    label: payload.data.label,
                    script: await new Promise<string>((resolve, reject) => {
                      const reader = new FileReader();
                      reader.onload = () => resolve(reader.result as string);
                      reader.onerror = reject;
                      reader.readAsText(payload.data.file);
                    }),
                  },
                },
                { isWebService }
              )
            : buildBrokerPayload(payload, { isWebService });

        const response = await axios.post(`/broker`, {
          Category: 'env-integration',
          Operation: 'add',
          ResourceKey: manager.assetKey,
          ...body,
        });
        return response.data;
      },
      defaultErrorMessage: 'Failed to add credential',
      errorByStatusCode: {
        403: MANAGE_INTEGRATIONS_REQUIRED_MESSAGE,
      },
    });

  // Complex-Login fan-out: submit one broker POST per identity via the shared
  // submitComplexLoginIdentities helper (mirrors the seed wizard's
  // useAddSeedSubmit). Per-identity failure is isolated — a thrown POST becomes
  // an ok:false result and does not abort the other identities.
  const { mutateAsync: submitComplexIdentities, status: complexSubmitStatus } =
    useMutation({
      mutationFn: async () =>
        submitComplexLoginIdentities({
          multi: complexLoginMulti,
          submitOne: makeComplexLoginSubmitOne(axios, manager.assetKey),
          limit: 4,
        }),
      defaultErrorMessage: 'Failed to add credentials',
    });

  const handleSubmit = async (payload: CredentialSubmitPayload) => {
    if (!canAddCredentials) {
      toast.error(addDisabledTooltip);
      return;
    }

    try {
      const promise = addCredential(payload);
      toast.promise(promise, {
        loading: 'Adding credential...',
        success: 'Credential added successfully',
        error: (error: unknown) =>
          getAddCredentialErrorMessage(error, 'Failed to add credential'),
      });

      await promise;

      manager.invalidateCredentials();
      handleCloseAdd(true);
    } catch (error) {
      console.error('Add credential error:', error);
    }
  };

  const handleSubmitComplexLogin = async () => {
    if (!canAddCredentials) {
      toast.error(addDisabledTooltip);
      return;
    }

    let results;
    try {
      results = await submitComplexIdentities();
    } catch {
      // submitComplexLoginIdentities isolates per-identity failures into
      // ok:false results, so a throw here is an UNEXPECTED (non-per-identity)
      // error. The useMutation wrapper re-throws mutationFn rejections, so
      // without this guard it would surface as an unhandled rejection, leaving
      // the modal stuck on the form step with nothing shown. Mirror the seed
      // wizard twin (useAddSeedSubmit.ts:648-656): surface a GENERIC toast and
      // return without advancing modalStep. Do NOT interpolate the caught error
      // — it may be an Axios error whose config.data carries credential
      // payloads (inputs/totpSecret).
      toast.error('Failed to start login recorders. Please try again.');
      return;
    }
    const succeeded = results.filter(r => r.ok && r.conversationId);
    // label only — NEVER interpolate inputs/totpSecret/error objects. The
    // Pick<> return type COMPILER-ENFORCES that no other IdentitySubmitResult
    // field (notably `error`) can be threaded through to the UI.
    const failed: Pick<IdentitySubmitResult, 'id' | 'label'>[] = results
      .filter(r => !r.ok || !r.conversationId)
      .map(({ id, label }) => ({ id, label }));

    // The broker persists each credential before spawning its recorder, so any
    // success means the list must refresh now — otherwise the new "Recording…"
    // rows are missing until onRecorderComplete fires (which never happens if
    // the user exits mid-recording).
    if (succeeded.length > 0) manager.invalidateCredentials();

    setRecorderEntries(
      succeeded.map(({ id, label, conversationId }) => ({
        id,
        label,
        conversationId: conversationId!,
      }))
    );
    setFailedIdentities(failed);
    // Partial/total failure keeps the modal open in the progress view.
    setModalStep('progress');
    if (failed.length > 0) {
      toast.error(
        `${failed.length} of ${results.length} logins failed to start`
      );
    }
  };

  const handleCloseAdd = (justAdded?: boolean) => {
    // If the user came from the list view, go back to the list
    // If they came from no modal (direct open to add), close entirely
    if (justAdded || manager.credentials.length > 0) {
      manager.onBackToList();
    } else {
      manager.onClose();
    }
    // C5 fix: cancel any previous pending timer before scheduling a new one.
    // Without this, reopening the add modal within 500ms would let the old
    // timer fire and wipe the new session's form state.
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setModalStep('type');
      resetCredentialForms();
      setRecorderEntries([]);
      setFailedIdentities([]);
      closeTimerRef.current = null;
    }, 500);
  };

  return (
    <>
      {manager.modalState.kind === 'list' && (
        <CredentialListModal
          open
          onClose={manager.onClose}
          credentials={manager.credentials}
          onAddClick={manager.onAddClick}
          onDelete={manager.handleDelete}
          canAddCredentials={canAddCredentials}
          addDisabledTooltip={addDisabledTooltip}
          defaultCredentialId={manager.defaultCredentialId}
          onSetDefault={
            manager.supportsDefault ? manager.handleSetDefault : undefined
          }
          recorderJobs={recorderJobs}
          onOpenRecorder={openRecorder}
        />
      )}
      {manager.modalState.kind === 'add' && (
        <AddCredentialModal
          isWebService={isWebService}
          isOpen
          onClose={handleCloseAdd}
          modalStep={modalStep}
          setModalStep={setModalStep}
          selectedType={selectedCredentialType}
          onTypeChange={setSelectedCredentialType}
          basicForm={basicForm}
          setBasicForm={setBasicForm}
          recordingForm={recordingForm}
          setRecordingForm={setRecordingForm}
          bearerTokenForm={bearerTokenForm}
          setBearerTokenForm={setBearerTokenForm}
          apiKeyForm={apiKeyForm}
          setApiKeyForm={setApiKeyForm}
          staticTokenForm={staticTokenForm}
          setStaticTokenForm={setStaticTokenForm}
          complexLoginMulti={complexLoginMulti}
          setComplexLoginMulti={setComplexLoginMulti}
          webAppUrl={webAppUrl}
          addCredentialStatus={addCredentialStatus}
          complexSubmitStatus={complexSubmitStatus}
          canAddCredentials={canAddCredentials}
          addDisabledTooltip={addDisabledTooltip}
          onSubmit={handleSubmit}
          onSubmitComplexLogin={handleSubmitComplexLogin}
          recorderEntries={recorderEntries}
          failedIdentities={failedIdentities}
          onRecorderComplete={() => {
            manager.invalidateCredentials();
          }}
          onBackToList={
            manager.credentials.length > 0 ? manager.onBackToList : undefined
          }
        />
      )}
    </>
  );
}
